import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { OrderItemQuotesApi } from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { selectedClientServiceIdAtom } from "@/store";

const apiClient = createApiClient(OrderItemQuotesApi);

export const useOrderItemQuotes = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const queryClient = useQueryClient();

  const useAddOrderItemsQuote = () =>
    useMutation({
      mutationKey: ["createOrderItemQuote"],
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        command: Parameters<
          typeof apiClient.orderItemQuotesControllerAddOrderItemsQuote
        >["0"]["orderItemQuoteAddCommand"];
      }) =>
        apiClient.orderItemQuotesControllerAddOrderItemsQuote({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          orderItemQuoteAddCommand: params.command,
        }),
      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  const useSendOrderItemQuotes = () =>
    useMutation({
      mutationKey: ["sendOrderItemQuote"],
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        command: Parameters<
          typeof apiClient.orderItemQuotesControllerSendOrderItemsQuotes
        >["0"]["orderItemQuotesSendCommand"];
      }) =>
        apiClient.orderItemQuotesControllerSendOrderItemsQuotes({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          orderItemQuotesSendCommand: params.command,
        }),
      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  const useDeleteOrderItemQuote = () =>
    useMutation({
      mutationKey: ["deleteOrderItemQuote"],
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        sandboxItemQuoteId: string;
      }) =>
        apiClient.orderItemQuotesControllerDeleteOrderItemsQuote({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          sandboxItemQuoteId: params.sandboxItemQuoteId,
        }),
      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  return {
    useAddOrderItemsQuote,
    useDeleteOrderItemQuote,
    useSendOrderItemQuotes,
  };
};
